import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import Quill from "quill";
import { Note } from "store/apis/notes";
import { formatDate } from "utils/formatDate";
import { NoteActions, RenderDescription } from "./UnifyNotes";

const EMPTY_ASSIGNEES: { assignee_name: string; assignee_uuid: string }[] = [];

type TicketNoteCardProps = {
  note: Note;
  quillRefItem: React.RefObject<Quill | undefined | null>;
  handlePinUpdate: (noteId: string, isPinned: boolean) => () => void;
  isArchived: boolean;
  setEditableNoteId: React.Dispatch<React.SetStateAction<string | null>>;
  resetNotes: () => void;
  isPinning: boolean;
  pinningNoteId: string | null;
  ticketAssignees?: { assignee_name: string; assignee_uuid: string }[];
  ticketResolved?: boolean;
};

export const TicketNoteCard = ({
  note,
  quillRefItem,
  handlePinUpdate,
  isArchived,
  setEditableNoteId,
  resetNotes,
  isPinning,
  pinningNoteId,
  ticketAssignees = EMPTY_ASSIGNEES,
  ticketResolved,
}: TicketNoteCardProps) => {
  const assigneeNames = ticketAssignees
    .map((assignee) => assignee.assignee_name)
    .join(", ");

  return (
    <div
      className={classNames(
        "t-py-4 t-px-4 t-rounded-lg t-border t-border-solid t-relative t-group t-flex t-gap-8 t-flex-col",
        {
          "t-border-purple-10 t-bg-purple-0": note.is_pinned,
          "t-border-neutral-0 t-bg-surface-lighter-grey": !note.is_pinned,
        }
      )}
    >
      <div className="t-flex t-gap-1 t-items-start">
        <div className="t-flex-1">
          <RenderDescription
            description={note.description}
            quillRefItem={quillRefItem}
          />
        </div>
        {ticketResolved && (
          <div className="t-h-10 t-flex t-items-center">
            <ToolTip text="Ticket resolved">
              <span>
                <GreentickGreenCircle className="t-text-green-70 t-flex-shrink-0" />
              </span>
            </ToolTip>
          </div>
        )}
        <NoteActions
          noteId={note.uuid}
          isPinned={note.is_pinned}
          handlePinUpdate={handlePinUpdate}
          isArchived={isArchived}
          setEditableNoteId={setEditableNoteId}
          resetNotes={resetNotes}
          isPinning={isPinning && pinningNoteId === note.uuid}
          isPinnedNote={note.is_pinned}
          noteCreatedBy={note.created_by?.uuid}
        />
      </div>
      <div className="t-flex t-justify-between t-items-center">
        <div className="t-text-body-sm t-text-text-30 t-flex t-gap-1.5 t-items-center">
          <span>{note.created_by?.name || ""}</span>
          <span>•</span>
          {ticketAssignees.length > 0 && (
            <>
              <span>Assigned to {assigneeNames}</span>
              <span>•</span>
            </>
          )}
          <span>{formatDate(note.created_at)}</span>
          {note.is_edited && (
            <span className="t-text-body-sm t-italic">(Edited)</span>
          )}
        </div>
      </div>
    </div>
  );
};
